<template>
  <div>
    <v-card color="cardbackground">
      <v-card-title class="pt-2">
        Operator
      </v-card-title>
      <v-card-text>
        <v-btn
          block
          color="primary"
          :disabled="!isOnline || disabled"
          @click="openMainDialog()">
          <v-icon class="mr-2">mdi-book-open-outline</v-icon>
          Open Editor
        </v-btn>
      </v-card-text>
    </v-card>

    <v-dialog
      v-model="showMainDialog"
      max-width="800px"
      transition="dialog-bottom-transition"
      persistent>
      <v-card flat color="cardbackground">
        <v-card-text class="pa-4">
          <egg-dialog-title
            title="Operator"
            subtitle="Editor"
            @close-clicked="closeMainDialog()" />

          <v-card flat>
            <v-card-text class="pa-4">
              <v-card class="py-1 px-4 mb-2">
                <v-data-table
                  fixed-header
                  :search="search"
                  :headers="lookupItemHeaders"
                  :items="lookupItems"
                  no-data-text="No Data Available">
                  <template v-slot:top>
                    <div class="mb-0 d-flex flex-wrap overflow-y-auto">
                      <v-text-field
                        class="mt-1"
                        v-model="search"
                        clearable
                        label="Search"
                        type="text"
                        prepend-icon="mdi-magnify"
                      />
                      <egg-action-buttons
                        class="mt-2 ml-4"
                        primary-button-icon="mdi-plus"
                        @primary-button-clicked="addItem()"
                        primary-button-text="Add"
                        show-large-buttons
                      />
                    </div>
                  </template>
                  <template v-slot:[`item.isEnabled`]="{ item }">
                    <div class="d-flex justify-center">
                      <v-icon v-if="item.isEnabled">mdi-checkbox-marked-outline</v-icon>
                      <v-icon v-else>mdi-checkbox-blank-outline</v-icon>
                    </div>
                  </template>
                  <template v-slot:[`item.action`]="{ item }">
                    <egg-action-buttons
                      primary-button-icon="mdi-square-edit-outline"
                      @primary-button-clicked="editItem(item)"
                      secondary-button-icon="mdi-trash-can-outline"
                      @secondary-button-clicked="confirmDeleteItem(item)"/>
                  </template>
                </v-data-table>
              </v-card>
            </v-card-text>
          </v-card>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-form ref="form" v-model="validForm">
      <v-dialog v-model="showChangeDialog" 
        scrollable 
        persistent
        max-width="600px">
        <v-card>
          <v-card-title class="text-h5">
             {{isAdd ? 'Add' : 'Edit'}} Operator
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="editedItem.farmCode"
                  label="Farm"
                  :disabled="!isAdd"
                  :items="farms"
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editedItem.operatorName"
                  label="Operator Name"
                  :rules="[rules.required]" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-checkbox 
                  v-model="editedItem.isEnabled" 
                  label="Active" />
              </v-col>
            </v-row>
          </v-card-text>
          <egg-card-actions
            primary-button-text="Save"
            secondary-button-text="Cancel"
            :disabled="!validForm"
            @primary-button-clicked="saveItem()"
            @secondary-button-clicked="showChangeDialog = false" />
        </v-card>
      </v-dialog>
      <v-dialog v-model="showDeleteDialog" persistent max-width="350">
        <v-card>
          <v-card-title class="text-h5">
            Confirm Delete
          </v-card-title>
          <v-card-text>
            Ok to Delete {{itemToDelete.operatorName}} for {{itemToDelete.farmCode}}?
          </v-card-text>
          <egg-card-actions
            primary-button-text="Yes"
            secondary-button-text="No"
            @primary-button-clicked="deleteItem()"
            @secondary-button-clicked="showDeleteDialog = false" />
        </v-card>
      </v-dialog>
    </v-form>
  </div>
</template>

<script>
import DialogTitle from './DialogTitle'
import ActionButtons from './ActionButtons'
import CardActions from './CardActions'
import eggApi from '../api/eggApi'
import { mapState, mapActions } from 'vuex'

export default {
   props: {
    disabled: {
      type: Boolean,
      default: true
    },
  },
  created() {
    this.rules = { ...this.$root.validationRules };
  },
  components: {
    'egg-dialog-title': DialogTitle,
    'egg-action-buttons': ActionButtons,
    'egg-card-actions': CardActions
  },
  data() {
    return {
      lookupItems: [],
      editedItem: {},
      itemToDelete: {},
      isAdd: false,
      rules: null,
      search: '',
      showDeleteDialog: false,
      showChangeDialog: false,
      showMainDialog: false,
      validForm: true
    }
  },
  computed: {
    ...mapState(['settings','isOnline']),
    farms() {
      return this.settings.farms.map(x=> x.farmCode);
    },
    lookupItemHeaders() {
      return [
        { text: 'Id', value: 'operatorId', sortable: true },
        { text: 'Farm', value: 'farmCode', sortable: true },
        { text: 'Active', value: 'isEnabled', sortable: true, align: 'center' },
        { text: 'Name', value: 'operatorName', sortable: true, align: 'center' },
        { text: 'Actions', value: 'action', sortable: false, align: 'center' }
      ];
    }
  },
  methods: {
    ...mapActions(['updateSettingsInStore']),
    addItem() {
      this.$refs.form.resetValidation();
      this.isAdd = true;
      this.editedItem = {
      };
      this.showChangeDialog = true;
    },
    closeMainDialog() {
      this.showMainDialog = false;
    },
    confirmDeleteItem(item) {
      this.itemToDelete = item;
      this.showDeleteDialog = true;
    },
    async deleteItem() {
      await eggApi.deleteFarmOperator(this.itemToDelete.operatorId);
      const isEnabledOnly = false;
      this.lookupItems = await eggApi.getFarmOperators(null, isEnabledOnly);

      const settings = { ...this.settings };
      settings.farmOperators = this.lookupItems;
      this.updateSettingsInStore(settings);

      this.showDeleteDialog = false;
    },
    editItem(item) {
      this.$refs.form.resetValidation();
      this.isAdd = false;
      this.editedItem = {
        operatorId: item.operatorId,
        farmCode: item.farmCode,
        isEnabled: item.isEnabled,
        operatorName: item.operatorName
      }
      this.showChangeDialog = true;
    },
    async openMainDialog() {
      const isEnabledOnly = false;
      this.lookupItems = await eggApi.getFarmOperators(null, isEnabledOnly);
      this.showMainDialog = true;
    },
    async saveItem() {
      let payload = {
          operatorId: this.editedItem.operatorId,
          farmCode: this.editedItem.farmCode,
          isEnabled: this.editedItem.isEnabled,
          operatorName: this.editedItem.operatorName
      };

      if (this.isAdd) {
        await eggApi.addFarmOperator(payload);
      } else {
        await eggApi.updateFarmOperator(payload);
      }
      const isEnabledOnly = false;
      this.lookupItems = await eggApi.getFarmOperators(null, isEnabledOnly);

      const settings = { ...this.settings };
      settings.farmOperators = this.lookupItems;
      this.updateSettingsInStore(settings);

      this.showChangeDialog = false;
    }
  }
}
</script>